import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type { Persona } from "@talenthop/shared";
import { ArrowRight, ShieldCheck, Eye, EyeOff } from "../components/icons.js";
import { useAuth } from "../lib/AuthContext.js";
import { personaHome } from "../components/RequireAuth.js";
import { ApiError } from "../lib/api.js";

export function Login(): ReactNode {
  const { login, completeMfa } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Set when the pricing page sent someone here mid-signup (they chose "Sign in" instead of
  // registering fresh on the paid-signup screen) — carry that intent past the login form so
  // paying for the pack they actually clicked isn't a step they have to remember to come back to.
  const buyCredits = Number(params.get("buyCredits"));
  const hasBuyIntent = Number.isFinite(buyCredits) && buyCredits > 0;

  /** Where to land after auth: Plans & usage (0094) for a 'user' persona carrying a buy intent,
   *  otherwise each persona's normal home. */
  const postLoginTarget = (persona: Persona): string =>
    persona === "user" && hasBuyIntent ? "/app/plans" : personaHome(persona);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // MFA step-up: set when the password was accepted but a second-factor code is still required.
  // `mfaMethod` decides how we prompt — an emailed code vs. an authenticator app.
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaMethod, setMfaMethod] = useState<"email" | "totp">("totp");
  const [mfaCode, setMfaCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [capsOn, setCapsOn] = useState(false);

  const isEmailMfa = mfaMethod === "email";

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (mfaToken) {
        const persona = await completeMfa(mfaToken, mfaCode.trim());
        navigate(postLoginTarget(persona), { replace: true });
        return;
      }
      // Read from the DOM, not state: browser autofill (after sign-out) fills the fields
      // without ever firing onChange, so state can be empty while the form looks filled —
      // which used to leave Sign in dead (QA #62). By submit time the values are real.
      const fd = new FormData(e.currentTarget as HTMLFormElement);
      const emailValue = String(fd.get("email") ?? email).trim();
      const passwordValue = String(fd.get("password") ?? password);
      const result = await login(emailValue, passwordValue);
      if ("mfaRequired" in result) {
        setMfaToken(result.mfaToken);
        setMfaMethod(result.method ?? "totp");
        setMfaCode("");
        return;
      }
      navigate(postLoginTarget(result.persona), { replace: true });
    } catch (err) {
      if (mfaToken && err instanceof ApiError && err.status === 401) {
        setError(
          isEmailMfa
            ? "That code didn't verify — check the code we emailed you (it may have expired) and try again."
            : "That code didn't verify — check your authenticator app and try again.",
        );
      } else if (err instanceof ApiError && err.status === 403) {
        // 403 carries the reason: an invited user who hasn't set a password yet, or a signup that is
        // unverified / awaiting approval / unpaid / declined. Show the server's own sentence.
        const serverMsg = (err.body as { message?: string } | undefined)?.message;
        setError(
          serverMsg && !/set your password/i.test(serverMsg)
            ? serverMsg
            : "Your account isn't active yet. Open the invitation email we sent you and follow the link to set your password first.",
        );
      } else if (err instanceof ApiError && err.status === 401) {
        setError("Invalid email or password.");
      } else if (err instanceof ApiError && err.status === 400) {
        setError(
          mfaToken
            ? isEmailMfa
              ? "Enter the 6-digit code we emailed you."
              : "Enter the 6-digit code from your authenticator app."
            : "Enter a valid email and a password of at least 8 characters.",
        );
      } else {
        setError("Could not sign in. Check your connection and try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-wrap t1-auth">
      <AuthBrand />
      <form className="auth-card" onSubmit={onSubmit}>
        <div className="auth-head">
          <span className="mark">
            <ShieldCheck />
          </span>
          <h1>Sign in</h1>
          <p>Welcome back to your hiring workspace.</p>
        </div>

        {hasBuyIntent && (
          <div className="banner info" style={{ marginBottom: 16 }}>
            <span>
              Sign in, then you'll go straight to choosing a plan.
            </span>
          </div>
        )}

        {error && (
          <div className="banner crit" style={{ marginBottom: 16 }}>
            <span>{error}</span>
          </div>
        )}

        {!mfaToken && (
          <>
            <div className="field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                className="input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
              />
            </div>

            <div className="field" style={{ marginBottom: 8 }}>
              <label htmlFor="password">Password</label>
              <div className="password-field">
                <input
                  id="password"
                  name="password"
                  className="input"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => setCapsOn(e.getModifierState("CapsLock"))}
                  onKeyUp={(e) => setCapsOn(e.getModifierState("CapsLock"))}
                  onBlur={() => setCapsOn(false)}
                  placeholder="Your password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  data-tip={showPassword ? "Hide password" : "Show password"}
                  data-tip-pos="left"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff /> : <Eye />}
                </button>
              </div>
              {capsOn && (
                <p className="auth-hint" role="status" style={{ marginTop: 6, textAlign: "left", color: "var(--warn)" }}>
                  Caps Lock is on.
                </p>
              )}
            </div>

            <div style={{ textAlign: "right", marginBottom: 14 }}>
              <Link to="/forgot-password" className="t1-auth-link">
                Forgot password?
              </Link>
            </div>
          </>
        )}

        {mfaToken && (
          <div className="field">
            <label htmlFor="mfa-code">{isEmailMfa ? "Email sign-in code" : "Two-factor code"}</label>
            <input
              id="mfa-code"
              className="input"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              autoFocus
              required
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
              placeholder="000000"
            />
            <p className="auth-hint" style={{ marginTop: 8, textAlign: "left" }}>
              {isEmailMfa
                ? "We emailed a 6-digit sign-in code to your inbox — it expires in 10 minutes. Check spam if it's not there."
                : "Enter the 6-digit code from your authenticator app."}{" "}
              <button
                type="button"
                className="btn sm ghost"
                onClick={() => {
                  setMfaToken(null);
                  setMfaCode("");
                  setError(null);
                }}
              >
                Use a different account
              </button>
            </p>
          </div>
        )}

        <button
          className="btn primary"
          type="submit"
          style={{ width: "100%", justifyContent: "center", marginTop: 4, gap: 8 }}
          aria-busy={submitting}
          disabled={
            // No emptiness check: autofilled fields are invisible to React state until the user
            // interacts, so gating on state kept the button dead after sign-out (QA #62). The
            // inputs are `required`, so native validation still blocks a genuinely empty submit.
            submitting || (mfaToken ? mfaCode.trim().length !== 6 : false)
          }
        >
          {submitting && <span className="btn-spin" aria-hidden="true" />}
          {submitting ? (mfaToken ? "Verifying…" : "Signing in…") : mfaToken ? "Verify code" : "Sign in"}
        </button>

        <p className="auth-hint">Trouble signing in? Contact your workspace administrator.</p>

        <p className="auth-hint t1-auth-new">
          New to TalentHop? <Link to="/signup">Create a free account</Link>
        </p>

        {/* Candidates never sign in — a clear, separate signpost for anyone who lands here by mistake
            (audit 00: it used to be small grey text). */}
        <div className="t1-auth-cand">
          <div>
            <b>Have an interview invite?</b>
            <span>Candidates don't need an account.</span>
          </div>
          <Link to="/find-my-link" className="btn ghost sm">
            Open my interview <ArrowRight />
          </Link>
        </div>
      </form>
    </div>
  );
}

/**
 * Theme 1 brand panel for the auth pages (login, forgot / set password, verify). On desktop it is
 * the left half of a split card — a slowly drifting forest/lime gradient carrying the mark and what
 * the product is; on phones it collapses to a short banner above the form. Decorative motion only,
 * and it stops for prefers-reduced-motion. Every auth page renders <AuthBrand/> then its card, so
 * the split layout lives in CSS (.auth-wrap.t1-auth), not in each page.
 */
export function AuthBrand(): ReactNode {
  return (
    <aside className="t1-auth-art" aria-label="TalentHop">
      <span className="t1-auth-orb o1" aria-hidden="true" />
      <span className="t1-auth-orb o2" aria-hidden="true" />
      <span className="t1-auth-orb o3" aria-hidden="true" />
      <div className="t1-auth-art-top">
        <span className="t1-auth-mark" aria-hidden="true">TH</span>
        <span className="t1-auth-word">TalentHop</span>
      </div>
      <div className="t1-auth-art-copy">
        <span className="t1-auth-eyebrow">AI recruiter platform</span>
        <p className="t1-auth-headline">Hire verified talent. The engine ranks — your team decides.</p>
        <ul className="t1-auth-points">
          <li>Resumes scored against your own criteria</li>
          <li>Voice AI interviews with live-person checks</li>
          <li>Every hiring decision stays human</li>
        </ul>
      </div>
    </aside>
  );
}
